import { DateTime } from 'luxon'

import { useContext } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useInvoice, { useInvoiceForm } from '@hooks/useInvoice'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditInvoiceModal'

type ModalCallbacks = {
  closeModal: () => void,
  createInvoice: (payload: HandleCallbackActionParams) => void,
  updateInvoice: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditInvoiceModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createInvoice, updateInvoice } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const invoice = digObject(modalPayload, 'invoice')

  const newRecord = !invoice?.id

  const customRequiredFields = newRecord ? [] : [
    { key: 'creditor_id' },
    { key: 'creditor_type' },
    { key: 'debtor_name' },
    { key: 'due_date' },
  ]

  const invoiceFormPayload = useInvoiceForm(invoice, {
    customRequiredFields, validateOn: 'change',
  })

  const {
    entityState,
    entityState: {
      address,
      dates: { created },
      debtor_name,
      due_date,
      notes,
      order_title,
      purchase_order,
      reference,
    },
    handlers,
    saveEnabled,
  } = invoiceFormPayload

  const invoicePayload = useInvoice(invoice)
  const {
    callbacks: {
      createInvoice: createFn,
      updateInvoice: updateFn,
    },
    creating,
    updating,
  } = invoicePayload

  const createOrUpdateInvoicePayload = {
    callbacks: {
      action: newRecord ? createFn : updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    shouldRedirect: true,
    toastText: `Invoice ${newRecord ? 'Created' : 'Updated'}`,
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header
        callbacks={callbacks}
        title={newRecord ? 'Create' : 'Edit'}
        titleSecondLine="Invoice"
      />

      <SidebarModal.Content>
        {!newRecord && (
          <>
            <Form.Field label="* Debtor Name">
              <input name="debtor_name" type="text" value={debtor_name} {...handlers} />
            </Form.Field>

            <Form.Field label="* Billing Address" marginTop="large">
              {/*
               // @ts-expect-error TS2322 */}
              <textarea name="address" style={{ height: 80, resize: 'vertical' }} value={address} {...handlers} />
            </Form.Field>
          </>
        )}

        <Form.Row marginTop={newRecord ? '' : 'large'}>
          <Form.Field label="* Due Date">
            <input
              name="due_date"
              type="date"
              value={due_date}
              {...handlers}
            />
          </Form.Field>

          <Form.Field label="Created Date">
            <input
              disabled
              name="created_date"
              type="text"
              value={created?.date || DateTime.now().toFormat('dd/MM/yyyy')}
              {...handlers}
            />
          </Form.Field>
        </Form.Row>

        <Form.Field label="Reference" marginTop="large">
          <input name="reference" type="text" value={reference} {...handlers} />
        </Form.Field>

        {!newRecord && (
          <Form.Field label="Title" marginTop="large">
            <input name="order_title" type="text" value={order_title} {...handlers} />
          </Form.Field>
        )}

        <Form.Field label="Purchase Order #" marginTop="large">
          <input name="purchase_order" type="text" value={purchase_order} {...handlers} />
        </Form.Field>

        <Form.Field label="Notes" marginTop="large">
          {/*
           // @ts-expect-error TS2322 */}
          <textarea name="notes" style={{ height: 80, resize: 'vertical' }} value={notes} {...handlers} />
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={creating || updating}
          loadingBubbleColor="white"
          onClick={
            newRecord
              ? () => createInvoice(createOrUpdateInvoicePayload)
              : () => updateInvoice(createOrUpdateInvoicePayload)
          }
          size="large"
        >
          {saveEnabled ? `${newRecord ? 'Create' : 'Update'} Invoice` : 'Complete Fields'}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditInvoiceModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
